import React, { useState, useRef } from 'react';
import { Box, Button, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, TextField, InputAdornment, FormControlLabel, Checkbox, Pagination, } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { changeUserStatus, getUserList, Meta, putLockUnlockUser, UserList,  } from './UserDetails.helper';
import styles from './UserDetails.module.css';
import { convertUnderscoreToTitle } from '../../../../utils/commonUtils';


const UserDetails: React.FC = () => {
  const [users, setUsers] = useState<UserList[]>([]);
  const [paginationDetails, setpaginationDetails] = useState<Meta | null>(null);
  const [search, setSearch] = useState<string>('');
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const searchTimeout = useRef<NodeJS.Timeout | null>(null);
  const navigate = useNavigate();
  const { t: tGeneral } = useTranslation('general');
  const { t: tUser } = useTranslation('user_config');

  const fetchUsers = async (searchTerm = '', pageNum = 1) => {
    try {
      const offset = (pageNum - 1) * pageSize;
      const res = await getUserList(pageSize, offset, searchTerm);
      setUsers(res.objects);
      setpaginationDetails(res.meta);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to fetch users:', error);
    }
  };

  React.useEffect(() => {
    fetchUsers();
  }, []);

  const handleNewUser = () => {
    navigate('/ai/configuration/userDetails');
  };

  const handleEdit = (userId: number) => {
    navigate('/ai/configuration/userDetails', { state: { userId } });
  };

  const handleStatusChange = (e: React.ChangeEvent<HTMLInputElement>, id: number) => {
    const checked = e.target.checked;
    changeUserStatus(id, { is_active: checked })
      .then(() => {
        setUsers(prev =>
          prev.map(user =>
            user.id === id ? { ...user, is_active: checked } : user
          )
        );
      })
      .catch((error) => { 
        // eslint-disable-next-line no-console
        console.error('Failed to update user status:', error);
      });
  };

  const handleLockChange = (e: React.ChangeEvent<HTMLInputElement>, id: number) => {
    const checked = e.target.checked;
    putLockUnlockUser(id, { action: checked ? 'lock' : 'unlock' })
      .then(() => {
        setUsers(prev =>
          prev.map(user =>
            user.id === id ? { ...user, is_locked: checked } : user
          )
        );
      })
      .catch((error) => { 
        // eslint-disable-next-line no-console
        console.error('Failed to update user status:', error);
      });
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearch(value);

    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }
    searchTimeout.current = setTimeout(() => {
      setPage(1); 
      fetchUsers(value, 1);
    }, 300);
  };

  const handleChangePage = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
    fetchUsers(search,value);
  };

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography 
          variant="h6"
          sx={{
            fontFamily: 'Montserrat',
            fontWeight: 700,
            fontStyle: 'bold',
            fontSize: '20px',
            lineHeight: '145%',
            letterSpacing: 0
          }}
        >
          {tUser('UserDetail_Page.Text_UserList')}
        </Typography>

        <Box display="flex" gap={2}>
          <TextField
            size="small"
            placeholder={tUser('UserDetail_Page.Text_SearchByUserName')}
            variant="outlined"
            value={search}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ width: 220 }}
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleNewUser}
            sx={{ backgroundColor: '#4B4B4B', borderRadius: 2 }}
          >
            {tUser('UserDetail_Page.Text_AddNewUsers')}
          </Button>
        </Box>
      </Box>

      <TableContainer component={Paper} elevation={0}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell className={styles.userTableHeader}>{tUser('UserDetail_Page.Text_UserTable.userName')}</TableCell>
              <TableCell className={styles.userTableHeader}>{tUser('UserDetail_Page.Text_UserTable.name')}</TableCell>
              <TableCell className={styles.userTableHeader}>{tUser('UserDetail_Page.Text_UserTable.role')}</TableCell>
              <TableCell className={styles.userTableHeader}>{tUser('UserDetail_Page.Text_UserTable.userType')}</TableCell>
              <TableCell className={styles.userTableHeader}>{tUser('UserDetail_Page.Text_UserTable.locked')}</TableCell>
              <TableCell className={styles.userTableHeader}>{tUser('UserDetail_Page.Text_UserTable.status')}</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user, idx) => (
              <TableRow key={idx}>
                <TableCell className={styles.userTableBody}>{user.username || '-'} </TableCell>
                <TableCell className={styles.userTableBody}>
                  {(user.first_name || user.last_name) ? `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim() : '-'}
                </TableCell>
                <TableCell className={styles.userTableBody}>{convertUnderscoreToTitle(user.role) || '-'} </TableCell>
                <TableCell className={styles.userTableBody}>{convertUnderscoreToTitle(user.user_type) || '-'} </TableCell>
                <TableCell>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={user.is_locked}
                        onChange={(e) => handleLockChange(e, user.id)}
                        sx={{
                          color: '#DEEFE1',
                          '&.Mui-checked': {
                            color: '#DEEFE1', 
                          },
                          '&.Mui-checked svg': {
                            fill: '#47B35F', 
                          },
                        }}
                      />
                    }
                    label=""
                  />
                </TableCell>
                <TableCell>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={user.is_active}
                        onChange={(e) => handleStatusChange(e, user.id)}
                        sx={{
                          color: '#DEEFE1', 
                          '&.Mui-checked': {
                            color: '#DEEFE1', 
                          },
                          '&.Mui-checked svg': {
                            fill: '#47B35F', 
                          },
                        }}
                      />
                    }
                    label=""
                  />
                </TableCell>
                <TableCell>
                  <IconButton color="primary" onClick={() => handleEdit(user.id)}>
                    <EditIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {users.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px', marginTop: '20px' }}>
          <Typography variant="body2">
            {tGeneral('Text_PaginationInfo', {
              start: (page - 1) * pageSize + 1,
              end: Math.min(page * pageSize, paginationDetails?.total_count ?? 0),
              total: paginationDetails?.total_count ?? 0
            })}
          </Typography>
          <Pagination
            count={Math.ceil((paginationDetails?.total_count ?? 0) / pageSize)}
            page={page}
            onChange={handleChangePage}
            siblingCount={0}
            boundaryCount={1}
            shape="rounded"
            sx={{
              '& .MuiPaginationItem-root': {
                width: 40,
                height: 32,
                fontSize: 14,
                borderRadius: '8px',
              },
              '& .MuiPaginationItem-root.Mui-selected': {
                backgroundColor: 'rgba(119, 119, 119, 1)',
                color: 'white',
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default UserDetails;
